import React, { useEffect, useState } from "react";
import { db } from "../firebaseConfig";
import { doc, getDoc, setDoc } from "firebase/firestore";
import Loader from "../components/Loader";

function ManagePermissions() {
  const [loading, setLoading] = useState(true);
  
  // 1. Roles
  const roles = ["Admin", "Manager", "Sales Executive", "Service Engineer", "Accountant", "Store Keeper"];
  
  // 2. MODULES (Synced with Sidebar)
  const allModules = [
      {
          category: "📊 DASHBOARD & BASICS",
          items: [
              { key: "dashboard", label: "Dashboard" },
              { key: "calendar", label: "Calendar / Planner" },
              { key: "map_view", label: "Live Map View" },
          ]
      },
      {
          category: "📞 SALES & LEADS",
          items: [
              { key: "leads", label: "Leads Master" },
              { key: "orders", label: "Orders (PO)" },
              { key: "visits", label: "Daily Sales Visits" },
              { key: "demos", label: "Product Demos" },
              { key: "sales_analysis", label: "Sales Analysis" },
              { key: "catalogs", label: "Catalogs / Brochures" },
          ]
      },
      {
          category: "🛠️ SERVICE & SUPPORT",
          items: [
              { key: "tickets", label: "Service Tickets (Calls)" },
              { key: "service_reports", label: "Service Reports" },
              { key: "pms", label: "PMS Schedule" },
              { key: "installation", label: "Installations" },
              { key: "amc_cmc", label: "AMC / CMC Contracts" },
              { key: "spares", label: "Spare Parts Inventory" },
          ]
      },
      {
          category: "📦 OPERATIONS & LOGISTICS",
          items: [
              { key: "courier", label: "Courier / Dispatch" },
              { key: "organizations", label: "Organization Master" },
              { key: "asset_history", label: "Machine Kundali (Asset)" },
              { key: "org_history", label: "Hospital Kundali" },
          ]
      },
      {
          category: "💰 ACCOUNTS & FINANCE",
          items: [
              { key: "payment_due", label: "Payment Dues" },
              { key: "payment_coll", label: "Collections" },
              { key: "expenses", label: "Expense Claims" },
              { key: "advance", label: "Advance Requests" },
          ]
      },
      {
          category: "📝 HR & TEAM",
          items: [
              { key: "attendance", label: "Attendance & Logs" },
              { key: "leave", label: "Leave Requests" },
              { key: "travel", label: "Travel Logs" },
              { key: "emp_history", label: "Employee Kundali" },
          ]
      },
      {
          category: "⚙️ ADMIN CONTROL",
          items: [
              { key: "users", label: "Manage Users" },
              { key: "settings", label: "App Settings" },
              { key: "permissions", label: "Access Control" }
          ]
      }
  ];

  const [permissions, setPermissions] = useState({});

  // LOAD PERMISSIONS
  useEffect(() => {
    const fetchPerms = async () => {
        try {
            const docRef = doc(db, "settings", "permissions");
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                setPermissions(docSnap.data());
            } else {
                // Initialize default structure
                const initial = {};
                roles.forEach(r => initial[r] = {});
                setPermissions(initial);
            }
        } catch (e) {
            console.error("Error loading permissions:", e);
        }
        setLoading(false);
    };
    fetchPerms();
  }, []);

  // TOGGLE
  const handleToggle = (role, moduleKey) => {
      setPermissions(prev => ({
          ...prev,
          [role]: {
              ...prev[role],
              [moduleKey]: !prev[role]?.[moduleKey]
          }
      }));
  };

  // SAVE
  const handleSave = async () => {
      setLoading(true);
      try {
          await setDoc(doc(db, "settings", "permissions"), permissions);
          alert("Permissions Saved Successfully! ✅\nUsers may need to refresh the app.");
      } catch (e) {
          alert("Error saving permissions");
      }
      setLoading(false);
  };

  if (loading) return <Loader />;

  return (
    <div style={{padding:"20px", fontFamily:"'Segoe UI', sans-serif", maxWidth:"1200px", margin:"0 auto"}}>
      
      <div style={headerStyle}>
        <div>
            <h2 style={{margin:0, color:"#2c3e50"}}>🛡️ Role Access Control</h2>
            <p style={{margin:"5px 0", color:"gray", fontSize:"13px"}}>Control who sees what in the app sidebar.</p>
        </div>
        <button onClick={handleSave} style={btnSave}>💾 Save Changes</button>
      </div>

      <div style={{overflowX:"auto", background:"white", borderRadius:"8px", boxShadow:"0 4px 10px rgba(0,0,0,0.1)", maxHeight:"80vh"}}>
          <table style={tableStyle}>
              <thead style={{position:"sticky", top:0, zIndex:10}}>
                  <tr style={{background:"#2c3e50", color:"white"}}>
                      <th style={thStyle}>Module Name</th>
                      {roles.map(role => (
                          <th key={role} style={thStyle}>{role}</th>
                      ))}
                  </tr>
              </thead>
              <tbody>
                  {allModules.map((group, gIndex) => (
                      <React.Fragment key={gIndex}>
                          {/* Category Header */}
                          <tr style={{background:"#f1f5f9"}}>
                              <td colSpan={roles.length + 1} style={categoryRowStyle}>
                                  {group.category}
                              </td>
                          </tr>
                          {/* Items */}
                          {group.items.map(mod => (
                              <tr key={mod.key} style={rowStyle}>
                                  <td style={{...tdStyle, fontWeight:"500", color:"#444"}}>{mod.label}</td>
                                  {roles.map(role => {
                                      // Admin always has access visually
                                      const isChecked = role === 'Admin' ? true : (permissions[role]?.[mod.key] || false);
                                      const isDisabled = role === 'Admin'; 

                                      return (
                                          <td key={role + mod.key} style={{...tdStyle, textAlign:"center"}}>
                                              <input 
                                                  type="checkbox" 
                                                  checked={isChecked}
                                                  onChange={() => handleToggle(role, mod.key)}
                                                  style={{transform: "scale(1.4)", cursor: isDisabled ? "not-allowed" : "pointer", accentColor: "#27ae60"}}
                                                  disabled={isDisabled} 
                                              />
                                          </td>
                                      );
                                  })}
                              </tr>
                          ))}
                      </React.Fragment>
                  ))}
              </tbody>
          </table>
      </div>
    </div>
  );
}

// STYLES
const headerStyle={display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"20px", background:"#fff", padding:"15px", borderRadius:"8px", boxShadow:"0 2px 5px rgba(0,0,0,0.05)"};
const btnSave={backgroundColor:"#27ae60", color:"white", border:"none", padding:"12px 25px", borderRadius:"5px", cursor:"pointer", fontWeight:"bold", fontSize:"14px", boxShadow:"0 3px 6px rgba(0,0,0,0.1)"};

const tableStyle={width:"100%", borderCollapse:"collapse", minWidth:"900px"};
const thStyle={padding:"15px", borderLeft:"1px solid #3e5871", fontSize:"13px", textAlign:"center", textTransform:"uppercase", letterSpacing:"0.5px"};
const tdStyle={padding:"12px", borderLeft:"1px solid #eee", fontSize:"13px"};

const categoryRowStyle={padding:"12px 15px", fontWeight:"bold", color:"#2c3e50", borderBottom:"2px solid #e2e8f0", textTransform:"uppercase", fontSize:"12px", letterSpacing:"1px"};
const rowStyle={borderBottom:"1px solid #f0f0f0", transition:"0.1s"};

export default ManagePermissions;